import React, { useState, useEffect, useCallback } from 'react';
import { X, FileText, Check, CheckCircle, AlertCircle, Clock, Plus } from 'lucide-react';
import { getPrescriptionStatus, getOpenReimbursements, getPrescriptionHistory } from '../../../Services/APIs/reimbursement';
import PrescriptionForm from './PrescriptionForm';
import ClaimForm from './ClaimForm';
import ReuploadForm from './ReuploadForm';
import ReimbursementHistory from './ReimbursementHistory';
import ProofOfTreatmentCard from './ProofOfTreatmentCard';
import {
    statusLabel, statusStyle, nextStep, trackerSteps, historyLabel, fieldLabel, isTerminalStatus, reimbursementCards,
    PRESCRIPTION_APPROVED_LABEL, procedureStatus, approvedTotalOf, approvedProcedures, formatInr, toothLabel,
    waitingMessage, submittedProofDocuments, showsProcedures,
} from './reimbursementStatus';

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return '';
    }
};

const STEP_STYLE = {
    done: 'bg-green-500 text-white border-green-500',
    current: 'bg-purple-600 text-white border-purple-600 ring-4 ring-purple-100',
    attention: 'bg-amber-500 text-white border-amber-500 ring-4 ring-amber-100',
    failed: 'bg-red-500 text-white border-red-500',
    upcoming: 'bg-white text-gray-400 border-gray-200',
};

const STEP_LABEL_STYLE = {
    done: 'text-gray-700 font-semibold',
    current: 'text-purple-700 font-bold',
    attention: 'text-amber-700 font-bold',
    failed: 'text-red-700 font-bold',
    upcoming: 'text-gray-400',
};

// Done steps are ticked, the current one is highlighted, later ones are muted.
const Tracker = ({ status }) => (
    <ol className="flex items-start justify-between gap-1 mb-4" aria-label="Reimbursement progress">
        {trackerSteps(status).map(({ label, state }, index) => (
            <li key={label} data-state={state} className="flex-1 min-w-0 flex flex-col items-center text-center" aria-current={state === 'current' || state === 'attention' ? 'step' : undefined}>
                <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-bold ${STEP_STYLE[state]}`}>
                    {state === 'done' ? <Check className="w-3.5 h-3.5" /> : state === 'failed' ? <X className="w-3.5 h-3.5" /> : state === 'attention' ? <AlertCircle className="w-3.5 h-3.5" /> : index + 1}
                </span>
                <span className={`mt-1.5 text-[10px] leading-tight break-words ${STEP_LABEL_STYLE[state]}`}>{label}</span>
            </li>
        ))}
    </ol>
);

// After the member uploads proof of treatment: what they sent, and when.
const SubmittedProof = ({ reimbursement, step }) => {
    const documents = submittedProofDocuments(reimbursement.treatment_proof);
    return (
        <div className="border border-green-100 bg-green-50 rounded-xl p-3 mb-3" data-testid="submitted-proof">
            <p className="text-sm font-semibold text-green-800 flex items-center gap-1.5" data-testid="next-step-title">
                <CheckCircle className="w-4 h-4 flex-shrink-0" /> {step.title}
            </p>
            <p className="text-xs text-green-900 mt-1">{step.body}</p>
            {documents.length > 0 && (
                <ul className="mt-3 space-y-2">
                    {documents.map((doc) => (
                        <li key={doc.field} className="flex items-center gap-2 bg-white border border-green-100 rounded-lg px-3 py-2 text-xs">
                            <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="font-semibold text-gray-800 truncate" data-testid="submitted-proof-name">{doc.name}</p>
                                <p className="text-gray-400">
                                    {doc.name !== doc.label ? `${doc.label} · ` : ''}
                                    {doc.at ? `Uploaded ${formatDate(doc.at)}` : 'Uploaded'}
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// The procedures a ToothLens reviewer tabulated from the member's prescription, each with its
// decision: approved (and for how much), not approved (and why), or still under review.
const RecommendedProcedures = ({ reimbursement }) => {
    const procedures = reimbursement.procedures || [];
    if (!procedures.length) return null;
    const anyDecided = procedures.some((p) => p.decision !== 'pending');

    return (
        <div className="border border-gray-100 rounded-lg mb-3" data-testid="recommended-procedures">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide px-3 pt-3">Recommended procedures</p>
            <ul className="divide-y divide-gray-100">
                {procedures.map((procedure) => {
                    const status = procedureStatus(procedure);
                    return (
                        <li key={procedure._id} className="px-3 py-2 text-xs" data-testid="recommended-procedure">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="font-semibold text-gray-800">{procedure.treatment}</p>
                                    <p className="text-gray-400">
                                        {[toothLabel(procedure.tooth), procedure.clinical_finding, procedure.priority].filter(Boolean).join(' · ')}
                                    </p>
                                </div>
                                <span className={`flex-shrink-0 px-2 py-0.5 rounded-full border font-semibold ${status.style}`} data-testid="recommended-procedure-status">
                                    {status.label}
                                </span>
                            </div>
                            {procedure.decision === 'rejected' && procedure.decision_note && (
                                <p className="text-gray-500 mt-1">Reason: {procedure.decision_note}</p>
                            )}
                        </li>
                    );
                })}
            </ul>
            {anyDecided && (
                <p className="px-3 py-2 text-xs text-gray-600 border-t border-gray-100" data-testid="approved-total">
                    Approved for reimbursement: <strong className="text-gray-900">{formatInr(approvedTotalOf(reimbursement))}</strong>
                </p>
            )}
        </div>
    );
};

// One Reimbursement: where it stands, what to do next, and what has happened. Starting a new
// Reimbursement is offered once for the whole section, so a closed card shows no button of its own.
// Once the prescription is approved the next action (proof of treatment) gets its own card.
const LifecycleCard = ({ reimbursement, onAction, proofUpload, onProofUploaded }) => {
    const status = reimbursement.reimbursement_status;
    const step = nextStep(status);
    const action = step?.action === 'new' ? null : step?.action;
    const request = reimbursement.document_request;
    const approved = status && !status.startsWith('reimbursement_prescription_');
    const awaitingProof = status === 'reimbursement_treatment_pending';
    const proofSubmitted = status === 'reimbursement_treatment_review';

    return (
        <div className="border border-gray-100 rounded-xl p-4 mb-4" data-testid="reimbursement-current">
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                    <p className="font-bold text-gray-900 text-sm">{reimbursement.clinic_name || 'Your reimbursement'}</p>
                    <p className="text-xs text-gray-400">Requested {formatDate(reimbursement.createdAt)}</p>
                </div>
                <span data-testid="reimbursement-status" className={`text-xs font-semibold px-2.5 py-1 rounded-full border text-right ${statusStyle(status)}`}>
                    {statusLabel(status)}
                </span>
            </div>

            <Tracker status={status} />

            {/* While awaiting proof the status badge already says "Prescription Approved". */}
            {approved && !awaitingProof && (
                <p className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-2.5 py-1.5 mb-3 inline-flex items-center gap-1.5" data-testid="prescription-approved">
                    <Check className="w-3.5 h-3.5" /> {PRESCRIPTION_APPROVED_LABEL}
                </p>
            )}

            {awaitingProof && (
                <div className="flex gap-2 text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3" data-testid="action-required">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
                    <div>
                        <p className="text-sm font-semibold" data-testid="next-step-title">{step.title}</p>
                        <p className="text-xs mt-0.5">{step.body}</p>
                    </div>
                </div>
            )}

            {proofSubmitted && <SubmittedProof reimbursement={reimbursement} step={step} />}

            {step && !awaitingProof && !proofSubmitted && (
                <div className="mb-3">
                    <p className="text-sm font-semibold text-gray-800" data-testid="next-step-title">{step.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{step.body}</p>
                </div>
            )}

            {request && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3 mb-3" data-testid="reviewer-request">
                    <p className="font-semibold">Documents to re-upload: {request.fields.map(fieldLabel).join(', ')}</p>
                    <p className="mt-1">Reviewer note: “{request.note}”</p>
                </div>
            )}

            {!request && reimbursement.review_note && (
                <p className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-lg p-2 mb-3">Reviewer note: “{reimbursement.review_note}”</p>
            )}

            {showsProcedures(reimbursement) && <RecommendedProcedures reimbursement={reimbursement} />}

            {awaitingProof && (
                <ProofOfTreatmentCard
                    reimbursement={reimbursement}
                    uploaded={proofUpload}
                    onUploaded={onProofUploaded}
                    onContinue={(invoice) => onAction('upload_proof', reimbursement, { invoice })}
                />
            )}

            {action && !awaitingProof && (
                <button
                    onClick={() => onAction(action, reimbursement)}
                    data-testid={`next-step-${action}`}
                    className={`w-full py-2.5 text-sm font-semibold text-white rounded-xl transition-colors ${action === 'reupload' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                >
                    {step.actionLabel}
                </button>
            )}
            {!action && !isTerminalStatus(status) && (
                <p className="text-xs text-gray-400 flex items-center gap-1.5" data-testid="waiting-message">
                    <Clock className="w-3.5 h-3.5 flex-shrink-0" /> {waitingMessage(status)}
                </p>
            )}

            {reimbursement.status_history?.length > 0 && (
                <details className="mt-4">
                    <summary className="text-xs font-semibold text-purple-600 cursor-pointer">Show activity</summary>
                    <ol className="mt-2 space-y-2" data-testid="reimbursement-timeline">
                        {[...reimbursement.status_history].reverse().map((entry, i) => (
                            <li key={`${entry.at}-${i}`} className="text-xs">
                                <p className="font-semibold text-gray-700">{historyLabel(entry.action)}</p>
                                <p className="text-gray-400">{formatDate(entry.at)}{entry.role === 'reviewer' ? ' · ToothLens reviewer' : ''}</p>
                                {entry.note && <p className="text-gray-600 mt-0.5">“{entry.note}”</p>}
                            </li>
                        ))}
                    </ol>
                </details>
            )}
        </div>
    );
};

const ReimbursementSection = ({ onClose }) => {
    const [openReimbursements, setOpenReimbursements] = useState([]);
    const [latest, setLatest] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    // { mode: 'request' } | { mode: 'upload_proof', reimbursement, invoice } | { mode: 'reupload', reimbursement }
    const [view, setView] = useState(null);
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
    // Invoice already uploaded from a Reimbursement's proof card ({ file, url }), by Reimbursement id,
    // so it is still there if the member opens the proof form and closes it again.
    const [proofUploads, setProofUploads] = useState({});

    const setProofUpload = useCallback((id, invoice) => setProofUploads((prev) => {
        const next = { ...prev };
        if (invoice) next[id] = invoice;
        else delete next[id];
        return next;
    }), []);

    const fetchStatus = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const [openResponse, latestResponse] = await Promise.all([getOpenReimbursements(), getPrescriptionStatus()]);
            const records = openResponse.data?.data?.records;
            setOpenReimbursements(Array.isArray(records) ? records : []);
            const data = latestResponse.data?.data;
            setLatest(data && typeof data === 'object' && data._id ? data : null);
        } catch (err) {
            if (err?.response?.status !== 401) setError('Failed to load your reimbursements. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    const handleFormDone = () => {
        if (view?.reimbursement) setProofUpload(view.reimbursement._id, null);
        setView(null);
        fetchStatus();
        setHistoryRefreshKey((k) => k + 1);
    };

    const handleAction = (action, reimbursement, extra = {}) => setView({ mode: action, reimbursement, ...extra });
    const startNew = () => setView({ mode: 'request' });

    if (view?.mode === 'request') {
        return <PrescriptionForm onClose={() => setView(null)} onSuccess={handleFormDone} />;
    }
    if (view?.mode === 'upload_proof' && view.reimbursement) {
        return (
            <ClaimForm
                prescriptionId={view.reimbursement._id}
                approvedProcedures={approvedProcedures(view.reimbursement.procedures)}
                initialInvoice={view.invoice}
                onClose={() => setView(null)}
                onSuccess={handleFormDone}
            />
        );
    }
    if (view?.mode === 'reupload' && view.reimbursement?.document_request) {
        return <ReuploadForm reimbursement={view.reimbursement} onClose={() => setView(null)} onSuccess={handleFormDone} />;
    }

    const cards = reimbursementCards(openReimbursements, latest);

    return (
        <>
            {/* Header */}
            <div className="bg-purple-600 p-5 rounded-t-2xl flex justify-between items-center flex-shrink-0">
                <div>
                    <h2 className="text-lg font-bold text-white">Reimbursement</h2>
                    <p className="text-purple-100 text-xs mt-1">Treated outside the network? Get approved, then reimbursed.</p>
                </div>
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                >
                    <X className="w-4 h-4 text-white" />
                </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto flex-1" data-testid="reimbursement-section">
                {error && (
                    <p role="alert" className="text-red-600 text-sm bg-red-50 border border-red-100 rounded-lg p-3 mb-4">
                        {error} <button className="underline font-semibold" onClick={fetchStatus}>Retry</button>
                    </p>
                )}

                {isLoading && cards.length === 0 && (
                    <div className="border border-gray-100 rounded-xl p-4 mb-4 animate-pulse">
                        <div className="h-4 bg-gray-100 rounded w-1/2 mb-2" />
                        <div className="h-3 bg-gray-100 rounded w-3/4" />
                    </div>
                )}

                {!isLoading && !error && cards.length === 0 && (
                    <div className="border border-gray-100 rounded-xl p-4 mb-4" data-testid="reimbursement-empty">
                        <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-4 h-4 text-purple-500" />
                            <p className="font-bold text-gray-900 text-sm">No reimbursement yet</p>
                        </div>
                        <ol className="text-xs text-gray-500 space-y-1 mb-3 list-decimal list-inside">
                            <li>Share your dentist's prescription — we review it before your treatment.</li>
                            <li>Once approved, get the treatment and upload proof of treatment.</li>
                            <li>We review it and reimburse you.</li>
                        </ol>
                        <button
                            onClick={startNew}
                            data-testid="next-step-new"
                            className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors"
                        >
                            Request reimbursement
                        </button>
                    </div>
                )}

                {cards.length > 0 && (
                    <button
                        onClick={startNew}
                        data-testid="start-new-reimbursement"
                        className="w-full py-2.5 mb-4 text-sm font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl transition-colors flex items-center justify-center gap-1.5"
                    >
                        <Plus className="w-4 h-4" /> Start a new reimbursement
                    </button>
                )}

                {cards.map((reimbursement) => (
                    <LifecycleCard
                        key={reimbursement._id}
                        reimbursement={reimbursement}
                        onAction={handleAction}
                        proofUpload={proofUploads[reimbursement._id] || null}
                        onProofUploaded={(invoice) => setProofUpload(reimbursement._id, invoice)}
                    />
                ))}

                <ReimbursementHistory
                    title="Past reimbursements"
                    icon={<FileText className="w-4 h-4 text-purple-500" />}
                    apiFn={getPrescriptionHistory}
                    refreshKey={historyRefreshKey}
                    excludeIds={cards.map((r) => r._id)}
                    renderDetails={(r) => r.clinic_name || '—'}
                />
            </div>
        </>
    );
};

export default ReimbursementSection;
